"use client";

import type { HeadingLevel } from "@components/core/Heading";
import type { ILink } from "@components/core/Link";
import type React from "react";

import { AspectRatio } from "@components/core/AspectRatio";
import { Heading } from "@components/core/Heading";
import { Link } from "@components/core/Link";
import { ProductIcon } from "@components/core/ProductIcon";
import { isValidImage } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

type ScrollerCardItem = {
  child?: React.ReactNode;
  description?: React.ReactNode | null | string;
  kicker?: React.ReactNode | null | string;
  links?: ILink[];
  title?: React.ReactNode | null | string;
  withBorder?: boolean;
};

interface ScrollerCardsProps {
  alternateCardAlignment?: boolean; // alternatives between left & right content vs asset alignment. Defaults to false.
  border?: boolean;
  className?: string;
  headingLevel?: HeadingLevel;
  inverse?: boolean;
  items: ScrollerCardItem[];
  tag?: "div" | "section";
  variant?: "landscape" | "square";
}

const ScrollerCardItem = ({
  item,
  variant,
  inverse,
  reverse,
  border,
  headingLevel = 2,
  index,
}: {
  border?: boolean;
  headingLevel?: HeadingLevel;
  index: number;
  inverse?: boolean;
  item: ScrollerCardItem;
  reverse?: boolean;
  variant?: "landscape" | "square";
}) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const squareVariant = useMemo(() => variant === "square", [variant]);
  const posthog = usePostHog();
  const [hasCaptured, setHasCaptured] = useState(false);

  useEffect(() => {
    const target = targetRef.current;
    const title = item.title;
    if (!target || hasCaptured || !title) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          posthog?.capture(`website_scrolled_to_scrollercarditem`, {
            textContent: title,
          });
          setHasCaptured(true);
        }
      },
      {
        threshold: 0.75,
      },
    );

    observer.observe(target);

    return () => {
      observer.unobserve(target);
    };
  }, [hasCaptured, item.title, posthog]);

  function onButtonClick(label?: string) {
    if (!label) {
      return;
    }
    posthog?.capture(`website_clicked_scrollercarditem_button`, {
      textContent: label,
    });
  }

  return (
    <div className='grid-layout lg:space-y-40' key={index} ref={targetRef}>
      <div className={cn("grid-col-span-12 space-y-60", "lg:grid-col-span-5", reverse && "lg:order-3")}>
        <div
          className={cn("group flex h-full flex-col items-center justify-center align-middle")}
          key={`inner-${index}`}
        >
          <div className='w-full'>
            {item.child && !squareVariant && (
              <AspectRatio border={border} className='mb-20 lg:hidden' ratio='3x2'>
                {item.child}
              </AspectRatio>
            )}
            {item.kicker && (
              <Heading
                className='font-bold uppercase text-lightest'
                level={Math.min(Number(headingLevel) + 1, 6).toString() as HeadingLevel}
              >
                {item.kicker}
              </Heading>
            )}
            {item.title && (
              <Heading className={squareVariant ? "f-heading-03" : "f-heading-04"} level={headingLevel}>
                {item.title}
              </Heading>
            )}
            {item.description && (
              <p className={cn("f-description-02 whitespace-pre-line", squareVariant ? "mt-12" : "mt-8")}>
                {item.description}
              </p>
            )}
            {item.links && item.links.length > 0 && (
              <div className='mt-20 flex flex-wrap items-center gap-8'>
                {item.links.map((link, index) => (
                  <Link
                    {...link}
                    ariaLabel={link.ariaLabel}
                    asButton
                    buttonVariants={{
                      variant: link.appearance || (inverse ? "secondary-inverse" : "secondary"),
                    }}
                    className={cn(isValidImage(link.img) ? "h-52 pl-6" : "", inverse ? "bg-overlay-light" : "")}
                    href={link.href}
                    key={index}
                    onClick={() => onButtonClick(link.label)}
                  >
                    <ProductIcon className='my-6 mr-8' icon={link.img} />
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
            {item.child && squareVariant && (
              <AspectRatio border={border} className='mt-28 block lg:hidden' ratio='1x1'>
                {item.child}
              </AspectRatio>
            )}
          </div>
        </div>
      </div>
      <div className={cn("lg:grid-col-span-1", reverse && "lg:order-2")} />
      <div className={cn("hidden lg:block", "lg:grid-col-span-6", reverse && "lg:order-1")}>
        <div className='flex w-full items-center overflow-hidden rounded-lg'>
          <AspectRatio
            border={item.withBorder}
            className={cn("w-full transform-gpu", "effect-opacity")}
            data-asset-index={index}
            key={index}
            ratio={squareVariant ? "1x1" : "3x2"}
          >
            {item.child}
          </AspectRatio>
        </div>
      </div>
    </div>
  );
};

const ScrollerCards = ({
  tag,
  className,
  headingLevel,
  border = false,
  items = [],
  inverse = false,
  variant = "square",
  alternateCardAlignment: alternateNarrativeAlignment = false,
}: ScrollerCardsProps) => {
  const Tag = tag || "section";
  const wrapperRef = useRef<HTMLDivElement>(null);

  return (
    <Tag className={cn("container flex flex-col gap-100", className)} ref={wrapperRef}>
      {items.map((item, index) => (
        <ScrollerCardItem
          border={border}
          headingLevel={headingLevel}
          index={index}
          inverse={inverse}
          item={item}
          key={index}
          reverse={alternateNarrativeAlignment ? index % 2 !== 0 : false}
          variant={variant}
        />
      ))}
    </Tag>
  );
};

ScrollerCards.displayName = "ScrollerCards";

export { ScrollerCards };
